import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FaFacebookF, FaInstagram, FaYoutube } from "react-icons/fa";
import { FiMail, FiPhone } from "react-icons/fi";
import logoRata from "../images/logoRata.png";
import { RACE_DATE_LABEL, RACE_EDITION, RACE_YEAR } from "../data/EventConfig";

const Section = styled.footer`
  background: linear-gradient(180deg, #0a2a4a 0%, #061a30 100%);
  color: #e9eef5;
  padding: 4rem 1.5rem 0;
`;

const Wrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const TopGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr;
  gap: 3rem;
  padding-bottom: 3rem;

  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
  }

  @media screen and (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Brand = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;

  img {
    width: 56px;
    height: 56px;
    object-fit: contain;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  }

  h3 {
    color: #ffce2d;
    font-size: 1.2rem;
    font-weight: 800;
    line-height: 1.15;
  }

  p {
    color: rgba(233, 238, 245, 0.7);
    font-size: 0.85rem;
    margin-top: 2px;
  }
`;

const Tagline = styled.p`
  color: rgba(233, 238, 245, 0.75);
  font-size: 0.95rem;
  line-height: 1.55;
  max-width: 320px;
`;

const ColTitle = styled.h4`
  color: #ffce2d;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-weight: 700;
  margin-bottom: 1.25rem;
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const NavItem = styled.li`
  a {
    color: rgba(233, 238, 245, 0.8);
    text-decoration: none;
    font-size: 0.92rem;
    transition: color 0.2s ease, transform 0.2s ease;
    display: inline-block;

    &:hover {
      color: #ffce2d;
      transform: translateX(2px);
    }
  }
`;

const ContactRow = styled.a`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: rgba(233, 238, 245, 0.8);
  text-decoration: none;
  font-size: 0.9rem;
  margin-bottom: 0.7rem;
  transition: color 0.2s ease;

  &:hover {
    color: #ffce2d;
  }

  svg {
    color: #faa71e;
    flex-shrink: 0;
  }
`;

const ContactName = styled.div`
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  margin-top: 0.75rem;
  margin-bottom: 0.25rem;

  &:first-child {
    margin-top: 0;
  }
`;

const Socials = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const SocialBtn = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 206, 45, 0.12);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffce2d;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #ffce2d;
    color: #0a2a4a;
    transform: translateY(-2px);
  }
`;

const Bottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1.5rem 0;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
  align-items: center;
  font-size: 0.82rem;
  color: rgba(233, 238, 245, 0.6);

  a {
    color: rgba(233, 238, 245, 0.7);
    text-decoration: none;
    margin-left: 1.25rem;

    &:hover {
      color: #ffce2d;
    }
  }
`;

const Footer = () => {
  return (
    <Section>
      <Wrap>
        <TopGrid>
          <Brand>
            <LogoRow>
              <img src={logoRata} alt="Cursa de Rățuște pe Bega" />
              <div>
                <h3>Cursa de Rățuște pe Bega</h3>
                <p>{RACE_EDITION} · {RACE_DATE_LABEL}</p>
              </div>
            </LogoRow>
            <Tagline>
              Eveniment caritabil organizat de Rotaract Club Timișoara. Adoptă o
              rățușcă, vino pe malurile Begăi de Ziua Copilului și ajută Secția
              de Neonatologie a Spitalului „Louis Țurcanu”.
            </Tagline>
            <Socials>
              <SocialBtn
                href="https://www.facebook.com/RotaractClubTimisoara"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FaFacebookF size={16} />
              </SocialBtn>
              <SocialBtn
                href="https://www.instagram.com/rotaractclubtimisoara/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <FaInstagram size={16} />
              </SocialBtn>
              <SocialBtn
                href="https://www.youtube.com/results?search_query=cursa+ratustelor+pe+bega"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <FaYoutube size={16} />
              </SocialBtn>
            </Socials>
          </Brand>

          <div>
            <ColTitle>Navigare</ColTitle>
            <NavList>
              <NavItem><Link to="/#cauza">Cauza 2026</Link></NavItem>
              <NavItem><Link to="/#istoric">Ediții anterioare</Link></NavItem>
              <NavItem><Link to="/#sponsori">Sponsori</Link></NavItem>
              <NavItem><Link to="/#faq">Întrebări frecvente</Link></NavItem>
              <NavItem><Link to="/rotaract">Despre Rotaract</Link></NavItem>
            </NavList>
          </div>

          <div>
            <ColTitle>Contact</ColTitle>
            <ContactName>Armand Lesll · Project Manager</ContactName>
            <ContactRow href="mailto:armand.lesll@rotaract.ro">
              <FiMail size={15} />
              <span>armand.lesll@rotaract.ro</span>
            </ContactRow>
            <ContactRow href="tel:+40734921915">
              <FiPhone size={15} />
              <span>0734 921 915</span>
            </ContactRow>
            <ContactName>Radu Bîrlan · Fundraising</ContactName>
            <ContactRow href="mailto:radu.birlan@rotaract.ro">
              <FiMail size={15} />
              <span>radu.birlan@rotaract.ro</span>
            </ContactRow>
            <ContactRow href="tel:+40755135500">
              <FiPhone size={15} />
              <span>0755 135 500</span>
            </ContactRow>
            <ContactName>Bogdan Dobre · Departament Tehnic</ContactName>
            <ContactRow href="mailto:bogdan.dobre@rotaract.ro">
              <FiMail size={15} />
              <span>bogdan.dobre@rotaract.ro</span>
            </ContactRow>
            <ContactRow href="tel:+40728143986">
              <FiPhone size={15} />
              <span>0728 143 986</span>
            </ContactRow>
          </div>

          <div>
            <ColTitle>Legal</ColTitle>
            <NavList>
              <NavItem>
                <Link to="/termeni-si-conditii">Termeni și condiții</Link>
              </NavItem>
              <NavItem>
                <Link to="/politica-de-confidentialitate">
                  Politica de confidențialitate
                </Link>
              </NavItem>
            </NavList>
          </div>
        </TopGrid>

        <Bottom>
          <span>
            © {RACE_YEAR} Rotaract Club Timișoara · Toate drepturile rezervate
          </span>
          <span>Plăți securizate prin Stripe</span>
        </Bottom>
      </Wrap>
    </Section>
  );
};

export default Footer;
